import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  FUND_INACTIVATION_REASON_LABELS,
  formatInactivationDate,
  isAwaitingConfirmationStatus,
  isPendingStatus,
  mapApiReasonToInternal,
} from 'src/app/helpers/fund-operational-status';
import { ApiService } from 'src/app/services/api/api.service';
import { FundService } from 'src/app/services/fund/fund.service';
import { ToastService } from 'src/app/services/toast/toast.service';

type TransferAdministratorDetails = {
  razaoSocial: string;
  cnpj: string;
  contato: string;
  email: string;
  telefone: string;
  celular: string;
};

type TransferReasonKind =
  | 'transfer'
  | 'closure'
  | 'other-inactivate'
  | 'receipt'
  | 'other-activate';

export type TransferView = {
  type: string;
  transferDate: string;
  assemblyDate: string;
  reason: string;
  observation: string;
  reasonKind: TransferReasonKind;
  hasAdministratorFields: boolean;
  administrator: TransferAdministratorDetails;
  status?: string | null;
  statusDisplay?: string | null;
};

@Component({
  selector: 'app-dashboard-fund-approval-transfers',
  templateUrl: './dashboard-fund-approval-transfers.component.html',
  styleUrls: ['./dashboard-fund-approval-transfers.component.scss'],
})
export class DashboardFundApprovalTransfersComponent
  implements OnInit, OnChanges
{
  @Input() canChange = false;
  @Input() refreshKey = 0;
  @Output() edit = new EventEmitter<TransferView>();
  @Output() cancel = new EventEmitter<TransferView>();
  @Output() confirm = new EventEmitter<TransferView>();

  loading = true;
  transfers: TransferView[] = [];
  private expandedTransferIndexes = new Set<number>();

  constructor(
    public fundService: FundService,
    private api: ApiService,
    private route: ActivatedRoute,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.getFundData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.refreshKey && !changes.refreshKey.firstChange) {
      this.getFundData();
    }
  }

  canExpand(transfer: TransferView): boolean {
    return transfer.hasAdministratorFields || this.hasObservation(transfer);
  }

  canEditTransfer(transfer: TransferView): boolean {
    return (
      this.canChange &&
      transfer.reasonKind === 'transfer' &&
      (isPendingStatus(transfer.status) ||
        isAwaitingConfirmationStatus(transfer.status) ||
        isAwaitingConfirmationStatus(transfer.statusDisplay))
    );
  }

  canCancelTransfer(transfer: TransferView): boolean {
    return this.canEditTransfer(transfer);
  }

  canConfirmInactivation(transfer: TransferView): boolean {
    return (
      this.canChange &&
      transfer.reasonKind === 'transfer' &&
      (isAwaitingConfirmationStatus(transfer.status) ||
        isAwaitingConfirmationStatus(transfer.statusDisplay))
    );
  }

  hasRowActions(transfer: TransferView): boolean {
    return (
      this.canEditTransfer(transfer) ||
      this.canCancelTransfer(transfer) ||
      this.canConfirmInactivation(transfer)
    );
  }

  hasObservation(transfer: TransferView): boolean {
    return Boolean(transfer.observation) && transfer.observation !== '--';
  }

  private hasTransferContent(transfer: TransferView): boolean {
    return (
      transfer.type !== '--' ||
      transfer.transferDate !== '--' ||
      transfer.assemblyDate !== '--' ||
      transfer.reason !== '--' ||
      this.canExpand(transfer)
    );
  }

  isTransferExpanded(index: number): boolean {
    return this.expandedTransferIndexes.has(index);
  }

  toggleTransferHistory(index: number): void {
    if (this.expandedTransferIndexes.has(index)) {
      this.expandedTransferIndexes.delete(index);
      return;
    }

    this.expandedTransferIndexes.add(index);
  }

  editTransfer(index: number, event?: Event): void {
    event?.stopPropagation();
    const transfer = this.transfers[index];
    if (transfer && this.canEditTransfer(transfer)) this.edit.emit(transfer);
  }

  cancelTransfer(index: number, event?: Event): void {
    event?.stopPropagation();
    const transfer = this.transfers[index];
    if (transfer && this.canCancelTransfer(transfer)) {
      this.cancel.emit(transfer);
    }
  }

  confirmTransfer(index: number, event?: Event): void {
    event?.stopPropagation();
    const transfer = this.transfers[index];
    if (transfer && this.canConfirmInactivation(transfer)) {
      this.confirm.emit(transfer);
    }
  }

  async getFundData(): Promise<void> {
    this.loading = true;
    try {
      const data = await this.api.get({
        route: 'api/v2/approvals/version/',
        params: {
          fund: this.route.snapshot.params.id,
          tab: 'transference',
        },
        token: true,
      });

      this.transfers = this.extractTransferEntries(data)
        .map((entry) => this.mapTransfer(entry))
        .filter((transfer) => this.hasTransferContent(transfer));

      this.fundService.fundData = {
        ...this.fundService.fundData,
        transfer: this.transfers as any,
      };
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        'Erro ao carregar transferência: ' + error
      );
    } finally {
      this.loading = false;
    }
  }

  private extractTransferEntries(payload: any): any[] {
    const root = payload?.[0];
    const fundNode = root?.fund?.[0] ?? root?.fund;

    if (Array.isArray(fundNode?.transfer_logs) && fundNode.transfer_logs.length) {
      return fundNode.transfer_logs;
    }
    if (Array.isArray(root?.transfer_logs) && root.transfer_logs.length) {
      return root.transfer_logs;
    }
    if (Array.isArray(fundNode?.status_logs) && fundNode.status_logs.length) {
      return fundNode.status_logs;
    }
    if (Array.isArray(fundNode?.transfer_events)) {
      return fundNode.transfer_events;
    }
    if (Array.isArray(fundNode?.transfers)) return fundNode.transfers;
    if (Array.isArray(fundNode?.transfer)) return fundNode.transfer;
    if (Array.isArray(root?.status_logs) && root.status_logs.length) {
      return root.status_logs;
    }
    if (Array.isArray(root?.transfer_events)) return root.transfer_events;
    if (fundNode) return [fundNode];
    if (root) return [root];

    return [];
  }

  private mapTransfer(entry: any): TransferView {
    const reasonKind = this.resolveReasonKind(entry);
    const hasAdministratorFields =
      reasonKind === 'transfer' || reasonKind === 'receipt';

    return {
      type: this.getEventTypeLabel(reasonKind),
      transferDate: hasAdministratorFields
        ? this.displayDate(entry.transfer_date ?? entry.at)
        : '--',
      assemblyDate: hasAdministratorFields
        ? this.displayDate(entry.assembly_date)
        : '--',
      reason: this.getReasonLabel(reasonKind, entry),
      observation: this.getObservation(entry),
      reasonKind,
      hasAdministratorFields,
      administrator: hasAdministratorFields
        ? this.resolveAdministratorDetails(entry)
        : {
            razaoSocial: '--',
            cnpj: '--',
            contato: '--',
            email: '--',
            telefone: '--',
            celular: '--',
          },
      status: entry.status ?? entry.transfer_status ?? null,
      statusDisplay: entry.status_display ?? null,
    };
  }

  private resolveReasonKind(entry: any): TransferReasonKind {
    if (
      entry.is_transfer_in ||
      entry.direction === 'transfer-in' ||
      entry.transfer_type === 'transfer-in' ||
      entry.event === 'transfer-in'
    ) {
      return 'receipt';
    }

    if (
      entry.is_transfer_out ||
      entry.direction === 'transfer-out' ||
      entry.transfer_type === 'transfer-out' ||
      entry.event === 'transfer-out'
    ) {
      return 'transfer';
    }

    const mappedReason = mapApiReasonToInternal(entry.reason);
    if (mappedReason === 'transfer') return 'transfer';
    if (mappedReason === 'closure') return 'closure';

    const event = String(entry.event ?? '').toLowerCase();
    if (event === 'activate' || event === 'reactivate' || event === 'activation') {
      return 'other-activate';
    }

    if (mappedReason === 'other' || event === 'inactivation') {
      return 'other-inactivate';
    }

    return 'other-inactivate';
  }

  private getEventTypeLabel(reasonKind: TransferReasonKind): string {
    if (reasonKind === 'receipt' || reasonKind === 'other-activate') {
      return 'Ativado';
    }

    return 'Inativado';
  }

  private getReasonLabel(reasonKind: TransferReasonKind, entry: any): string {
    if (entry.reason_label) return entry.reason_label;

    const typeLabel = String(entry.transfer_type_label ?? '').trim();
    if (typeLabel && !/^(saída|saida|entrada)$/i.test(typeLabel)) {
      return typeLabel;
    }

    if (reasonKind === 'transfer') {
      return FUND_INACTIVATION_REASON_LABELS.transfer;
    }
    if (reasonKind === 'closure') {
      return FUND_INACTIVATION_REASON_LABELS.closure;
    }
    if (reasonKind === 'receipt') {
      return 'Recebimento de outro administrador';
    }

    return FUND_INACTIVATION_REASON_LABELS.other;
  }

  private getObservation(entry: any): string {
    const comment = this.displayValue(
      entry.comment ??
        entry.transfer_comment ??
        entry.justification ??
        entry.transfer_description ??
        entry.transfer?.comentario ??
        entry.transfer?.descricao
    );

    if (
      comment === 'Transferência operacional' ||
      comment === 'Recebimento operacional' ||
      comment === 'Reativação por transferência administrativa.'
    ) {
      return '--';
    }

    return comment;
  }

  private resolveAdministratorDetails(
    entry: any
  ): TransferAdministratorDetails {
    const details =
      entry.administrador_detalhes ??
      entry.transfer?.administrador_detalhes ??
      {};

    return {
      razaoSocial: this.displayValue(
        details.razaoSocial ??
          entry.administrator_name ??
          entry.last_company_name ??
          entry.administrador ??
          entry.transfer?.administrador
      ),
      cnpj: this.displayValue(
        details.cnpj ??
          entry.administrator_document ??
          entry.last_company_document
      ),
      contato: this.displayValue(
        details.contato ??
          entry.administrator_contact ??
          entry.last_company_contact
      ),
      email: this.displayValue(
        details.email ?? entry.administrator_email ?? entry.last_company_email
      ),
      telefone: this.formatPhoneDisplay(
        details.ddiTelefone ??
          entry.administrator_phone_ddi ??
          entry.last_company_telephone_ddi,
        details.telefone ??
          entry.administrator_phone ??
          entry.last_company_telephone_number
      ),
      celular: this.formatPhoneDisplay(
        details.ddiCelular ??
          entry.administrator_mobile_ddi ??
          entry.last_company_cellphone_ddi,
        details.celular ??
          entry.administrator_mobile ??
          entry.last_company_cellphone_number
      ),
    };
  }

  private displayValue(value?: string | null): string {
    const normalized = (value ?? '').toString().trim();
    return normalized || '--';
  }

  private displayDate(value?: string | null): string {
    if (!value || !String(value).trim()) return '--';
    const formatted = formatInactivationDate(value);
    return formatted === '-' ? '--' : formatted;
  }

  private formatPhoneDisplay(
    ddi?: string | null,
    number?: string | null
  ): string {
    const normalizedNumber = (number ?? '').toString().trim();
    if (!normalizedNumber) return '--';

    const normalizedDdi = (ddi ?? '').toString().trim().replace(/^\+/, '');
    return normalizedDdi
      ? `+${normalizedDdi} ${normalizedNumber}`
      : normalizedNumber;
  }
}
